import { useState, useMemo } from 'react';
import { useGetClaimLimitsQuery, useCreateClaimLimitMutation, useUpdateClaimLimitMutation, useDeleteClaimLimitMutation, useGetProductGroupsQuery } from '../../../../../api';

const useClaimLimit = () => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    const [activeFilters, setActiveFilters] = useState({
        responsibleName: '',
        startDate: null,
        endDate: null,
        productGroup: ''
    });

    const { data: productGroups } = useGetProductGroupsQuery();

    const { data, isLoading, refetch } = useGetClaimLimitsQuery({
        pageSize: lazyParams.rows,
        pageNumber: lazyParams.page,
        responsibleName: activeFilters.responsibleName || undefined,
        startDate: activeFilters.startDate ? activeFilters.startDate.toISOString() : undefined,
        endDate: activeFilters.endDate ? activeFilters.endDate.toISOString() : undefined,
        productGroup: activeFilters.productGroup || undefined
    });

    const [createClaimLimit] = useCreateClaimLimitMutation();
    const [updateClaimLimit] = useUpdateClaimLimitMutation();
    const [deleteClaimLimit] = useDeleteClaimLimitMutation();

    // Sadece client-side sıralama için kullanılacak
    const sortedData = useMemo(() => {
        const items = data?.data?.items || [];
        if (!lazyParams.sortField) return items;

        return [...items].sort((a, b) => {
            let aVal, bVal;

            // Özel alanlar için sıralama
            if (lazyParams.sortField === 'responsibleList') {
                aVal = a.responsibleList?.map((r) => r.name).join(', ') || '';
                bVal = b.responsibleList?.map((r) => r.name).join(', ') || '';
            } else {
                aVal = a[lazyParams.sortField] || '';
                bVal = b[lazyParams.sortField] || '';
            }

            const order = lazyParams.sortOrder === 1 ? 1 : -1;
            return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
        });
    }, [data, lazyParams.sortField, lazyParams.sortOrder]);

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    // Sıralama olayını işle
    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleFilterChange = (updatedFilters) => {
        const apiFilters = {
            responsibleName: updatedFilters.responsibleName || '',
            startDate: updatedFilters.startDate || null,
            endDate: updatedFilters.endDate || null,
            productGroup: updatedFilters.productGroup || ''
        };

        setActiveFilters(apiFilters);
        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1,
            sortField: null,
            sortOrder: null
        }));
    };

    const initFilters = () => {
        setActiveFilters({
            responsibleName: '',
            startDate: null,
            endDate: null,
            productGroup: ''
        });
        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1,
            sortField: null,
            sortOrder: null
        }));
    };

    const handleCreate = async (formData) => {
        try {
            const response = await createClaimLimit(formData).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error creating claim limit:', error);
            return false;
        }
    };

    const handleUpdate = async (formData) => {
        try {
            const response = await updateClaimLimit(formData).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error updating claim limit:', error);
            return false;
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await deleteClaimLimit(id).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error deleting claim limit:', error);
            return false;
        }
    };

    return {
        claimLimits: sortedData,
        originalData: data?.data?.items || [],
        totalCount: data?.data?.totalCount || 0,
        loading: isLoading,
        lazyParams,
        onPage,
        onSort,
        handleCreate,
        handleUpdate,
        handleDelete,
        refetch,
        activeFilters,
        handleFilterChange,
        initFilters,
        productGroups: productGroups?.data || []
    };
};

export default useClaimLimit;
